import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Grid, List, Filter, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import FloatingActions from '../components/FloatingActions';
import WalkCard from '../components/WalkCard';
import formatDuration from '../utils/formatDuration';
import categoryLabel, { distanceCategory } from '../utils/categoryLabels';
import tagLabel, { TAG_GROUPS } from '../utils/tagLabels';
import { resolveCity } from '../utils/cityName';
import { generateItemListSchema } from '../utils/seoData';

const BACKEND = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const CATEGORIES = [
  { label: 'Nature & Parks', slug: 'nature-parks' },
  { label: 'Hidden Gems', slug: 'hidden-gems' },
  { label: 'Architecture', slug: 'architecture' },
  { label: 'Food-Focused Walks', slug: 'food-focused-walks' },
  { label: 'Scenic Walks', slug: 'scenic-walks' },
  { label: 'Historic Walks', slug: 'historic-walks' },
  { label: 'Family-Friendly', slug: 'family-friendly' },
  { label: 'Sunset Walks', slug: 'sunset-walks' },
];

const WalkListPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const routeParams = useParams();
  const [walks, setWalks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 768);
  const [refreshKey, setRefreshKey] = useState(0);

  // Parse URL params for active filters
  const params = new URLSearchParams(location.search);
  const activeCategories = params.get('categories')?.split(',').filter(Boolean) || [];
  const activeTags = params.get('tags')?.split(',').filter(Boolean) || [];
  const activeTag = params.get('tag') || '';  // Single SEO tag filter
  const activeTime = params.get('time') || '';  // Best time filter
  const activeDistance = params.get('distance') || '';  // Distance category filter
  const activeCount = activeCategories.length + activeTags.length + (activeTag ? 1 : 0) + (activeTime ? 1 : 0) + (activeDistance ? 1 : 0);

  // Resolve location from route params
  const city = resolveCity({
    routeParam: routeParams.city,
    filterParam: params.get('city'),
  });
  const neighborhood = routeParams.neighborhood || '';
  const neighborhoodLabel = neighborhood
    ? neighborhood.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    : '';

  // Dynamic page title using resolved location
  const locationName = neighborhoodLabel || city.name;
  const activeTagLabel = activeTag ? activeTag.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '';
  const timeLabels = {
    early_morning: 'Early Morning', morning: 'Morning', midday: 'Midday',
    late_afternoon: 'Late Afternoon', sunset: 'Sunset', evening: 'Evening',
    weekdays: 'Weekdays', weekends: 'Weekends',
    spring: 'Spring', summer: 'Summer', fall: 'Fall', winter: 'Winter',
  };
  const activeTimeLabel = timeLabels[activeTime] || (activeTime ? activeTime.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '');
  const pageTitle = activeTime
    ? `Best walks at ${activeTimeLabel} in ${locationName}`
    : activeTag
      ? `Walks tagged: ${activeTagLabel}`
      : activeCategories.length === 1
        ? `${categoryLabel(activeCategories[0])} in ${locationName}`
        : activeCount > 1
          ? `Walks in ${locationName}`
          : `${locationName} Walks`;
  const pageSubtitle = activeTime
    ? `${locationName} walks best enjoyed during ${activeTimeLabel.toLowerCase()}`
    : activeTag
      ? `${locationName} walks matching "${activeTagLabel}"`
      : activeCount === 0
      ? neighborhoodLabel
        ? `Curated walks in ${neighborhoodLabel}, ${city.name}`
        : `Curated urban adventures across ${city.name}`
      : null;
  const metaTitle = activeCategories.length === 1
    ? `${categoryLabel(activeCategories[0])} in ${locationName} | Wanderloop`
    : `${locationName} Walks | Wanderloop`;
  const metaDescription = activeCategories.length === 1
    ? `Discover the best ${categoryLabel(activeCategories[0]).toLowerCase()} in ${locationName}. Curated walking routes and urban adventures.`
    : `Discover the best urban walks in ${locationName}. Curated routes for every neighborhood and vibe.`;

  // Build the current base path from route params
  const basePath = routeParams.neighborhood
    ? `/walks/${routeParams.country}/${routeParams.city}/${routeParams.neighborhood}`
    : routeParams.city
      ? `/walks/${routeParams.country}/${routeParams.city}`
      : routeParams.country
        ? `/walks/${routeParams.country}`
        : '/walks';

  // Update URL params without page reload
  const setFilters = (cats, tags) => {
    const p = new URLSearchParams();
    if (cats.length) p.set('categories', cats.join(','));
    if (tags.length) p.set('tags', tags.join(','));
    const qs = p.toString();
    navigate(qs ? `${basePath}?${qs}` : basePath, { replace: true });
  };

  const toggleCategory = (slug) => {
    const next = activeCategories.includes(slug)
      ? activeCategories.filter(c => c !== slug)
      : [...activeCategories, slug];
    setFilters(next, activeTags);
  };

  const toggleTag = (slug) => {
    const next = activeTags.includes(slug)
      ? activeTags.filter(t => t !== slug)
      : [...activeTags, slug];
    setFilters(activeCategories, next);
  };

  const clearAll = () => navigate(basePath, { replace: true });

  // Fetch walks from API with filters
  useEffect(() => {
    const fetchWalks = async () => {
      setLoading(true);
      try {
        const qp = new URLSearchParams({ limit: '100' });
        if (activeCategories.length) qp.set('categories', activeCategories.join(','));
        if (activeTags.length) qp.set('tags', activeTags.join(','));
        if (neighborhood) qp.set('region', neighborhoodLabel);

        const res = await fetch(`${BACKEND}/api/walks?${qp}`);
        if (!res.ok) throw new Error('Failed to fetch');
        const data = await res.json();

        setWalks((data.walks || []).map(w => ({
          id: w.id,
          slug: w.slug,
          country: w.country || 'us',
          city: w.city || 'los-angeles',
          neighborhood: w.neighborhood || '',
          title: w.title,
          description: w.description,
          practicalSummary: w.practical_summary || w.description,
          startingPoint: w.starting_point,
          endingPoint: w.ending_point,
          distance: w.distance_meters ? `${(w.distance_meters / 1609.34).toFixed(1)} mi` : null,
          duration: formatDuration(w.duration_minutes),
          difficulty: w.difficulty,
          distanceCategory: w.distance_category || distanceCategory(w.distance_meters),
          category: categoryLabel(w.categories?.[0]) || 'Walk',
          categories: w.categories || [],
          tags: w.tags || [],
          seoTags: (w.seo_tags || []).flatMap(t => t.includes(',') ? t.split(',').map(s => s.trim().toLowerCase().replace(/^#/, '')).filter(Boolean) : [t.replace(/^#/, '').toLowerCase()]),
          bestTimes: Array.isArray(w.best_times) ? w.best_times : (w.best_times ? [w.best_times] : []),
          region: w.region,
          photos: w.images?.length ? w.images : [w.main_image].filter(Boolean),
          main_image: w.main_image,
          featured: w.featured,
          userRatings: w.user_ratings || 0,
          favoriteCount: w.favorite_count || 0,
          completionCount: w.completion_count || 0,
        })));
      } catch (e) {
        console.error('Fetch walks failed:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchWalks();
  }, [location.search, neighborhood]);

  // Client-side filters for ?tag= and ?time= parameters
  let filteredWalks = walks;
  if (activeTag) {
    filteredWalks = filteredWalks.filter(w => (w.seoTags || []).some(t => t === activeTag || t.includes(activeTag) || activeTag.includes(t)));
  }
  if (activeTime) {
    filteredWalks = filteredWalks.filter(w => (w.bestTimes || []).includes(activeTime));
  }
  if (activeDistance) {
    filteredWalks = filteredWalks.filter(w => w.distanceCategory === activeDistance);
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title={metaTitle}
        description={metaDescription}
        url="/walks"
        structuredData={filteredWalks.length > 0 ? [generateItemListSchema(filteredWalks, pageTitle)] : undefined}
      />
      <Header />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 text-white py-12 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-1">{pageTitle}</h1>
              <p className="text-gray-300">
                {loading ? 'Loading...' : pageSubtitle || `${filteredWalks.length} walk${filteredWalks.length !== 1 ? 's' : ''} found`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={showFilters ? 'default' : 'outline'}
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className={`relative ${showFilters ? 'bg-amber-500 hover:bg-amber-600 text-white border-amber-500' : 'bg-white/10 text-white border-white/30 hover:bg-white/20'}`}
              >
                <Filter className="h-4 w-4 mr-1" />
                Filters
                {activeCount > 0 && (
                  <span className="absolute -top-2 -right-2 w-5 h-5 bg-amber-500 text-white rounded-full text-xs flex items-center justify-center">
                    {activeCount}
                  </span>
                )}
              </Button>
              <Button
                size="sm"
                onClick={() => setViewMode('grid')}
                className={viewMode === 'grid' ? 'bg-white/20 text-white border-white/30' : 'bg-white/10 text-white/60 border-white/20 hover:bg-white/20'}
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? 'bg-white/20 text-white border-white/30' : 'bg-white/10 text-white/60 border-white/20 hover:bg-white/20'}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filter Sidebar */}
          {showFilters && (
            <div className="lg:w-72 flex-shrink-0">
              <div className="bg-white rounded-lg shadow-sm border p-5 sticky top-20 max-h-[80vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">Filters</h3>
                  {activeCount > 0 && (
                    <button onClick={clearAll} className="text-xs text-amber-600 hover:underline">Clear all</button>
                  )}
                </div>

                {/* Categories */}
                <div className="mb-5">
                  <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-2">Categories</p>
                  <div className="flex flex-wrap gap-1.5">
                    {CATEGORIES.map(cat => (
                      <button
                        key={cat.slug}
                        onClick={() => toggleCategory(cat.slug)}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                          activeCategories.includes(cat.slug)
                            ? 'bg-amber-500 text-white border-amber-500'
                            : 'bg-white text-gray-600 border-gray-300 hover:border-amber-400'
                        }`}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Distance Category */}
                <div className="mb-5">
                  <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-2">Distance</p>
                  <div className="flex flex-wrap gap-1.5">
                    {['Short', 'Medium', 'Long', 'Epic'].map(d => (
                      <button
                        key={d}
                        onClick={() => {
                          const p = new URLSearchParams(location.search);
                          if (activeDistance === d) p.delete('distance');
                          else p.set('distance', d);
                          navigate(`${basePath}?${p.toString()}`, { replace: true });
                        }}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                          activeDistance === d
                            ? 'bg-amber-500 text-white border-amber-500'
                            : 'bg-white text-gray-600 border-gray-300 hover:border-amber-400'
                        }`}
                      >
                        {d} {d === 'Short' ? '(<2mi)' : d === 'Medium' ? '(2-5mi)' : d === 'Long' ? '(5-10mi)' : '(10+mi)'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tags by group */}
                {TAG_GROUPS.map(({ group, tags }) => (
                  <div key={group} className="mb-4">
                    <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-1.5">{group}</p>
                    <div className="flex flex-wrap gap-1">
                      {tags.map(tag => (
                        <button
                          key={tag}
                          onClick={() => toggleTag(tag)}
                          className={`px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors ${
                            activeTags.includes(tag)
                              ? 'bg-amber-500 text-white border-amber-500'
                              : 'bg-white text-gray-500 border-gray-200 hover:border-amber-400'
                          }`}
                        >
                          {tagLabel(tag)}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Walk Results */}
          <div className="flex-1">
            {/* Active filter pills */}
            {activeCount > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {activeCategories.map(c => (
                  <span key={c} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium">
                    {categoryLabel(c)}
                    <button onClick={() => toggleCategory(c)} className="p-0.5 rounded-full hover:bg-amber-200"><X className="w-3 h-3" /></button>
                  </span>
                ))}
                {activeTags.map(t => (
                  <span key={t} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium">
                    {tagLabel(t)}
                    <button onClick={() => toggleTag(t)} className="p-0.5 rounded-full hover:bg-amber-200"><X className="w-3 h-3" /></button>
                  </span>
                ))}
                <button onClick={clearAll} className="text-xs text-gray-500 hover:text-gray-700 px-2">Clear all</button>
              </div>
            )}

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => (
                  <div key={i} className="animate-pulse">
                    <div className="bg-gray-200 aspect-[4/3] rounded-lg mb-4" />
                    <div className="h-4 bg-gray-200 rounded mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                  </div>
                ))}
              </div>
            ) : filteredWalks.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-gray-500 text-lg mb-4">
                  {activeTime ? `No walks for ${activeTimeLabel} yet` : activeTag ? `No walks tagged "${activeTagLabel}"` : 'No walks match your filters'}
                </p>
                <Button onClick={clearAll} variant="outline">
                  {activeTag ? 'View All Walks' : 'Clear Filters'}
                </Button>
              </div>
            ) : (
              <div className={viewMode === 'grid'
                ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6'
                : 'space-y-6'
              }>
                {filteredWalks.map(walk => (
                  <WalkCard key={`${walk.id}-${refreshKey}`} walk={walk} onFavoriteToggle={() => setRefreshKey(r => r + 1)} />
                ))}
              </div>
            )}

            {/* Popular Tags */}
            {filteredWalks.length > 0 && activeTags.length === 0 && !activeTag && !activeTime && (
              <div className="mt-12 p-6 bg-white rounded-lg shadow-sm border">
                <h3 className="text-lg font-semibold mb-4">Popular Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {['dog-friendly', 'sunset', 'coastal', 'flat', 'photogenic', 'family-friendly', 'urban-grid', 'leisurely', 'transit-accessible', 'free'].map(tag => (
                    <button
                      key={tag}
                      onClick={() => { toggleTag(tag); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      className="px-3 py-1 bg-white border border-[#F5A623] text-[#F5A623] rounded-full text-sm transition-colors hover:bg-[#FFF8EE]"
                    >
                      {tagLabel(tag)}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-[#F5A623]/70 mt-3">Click a tag to filter walks</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <FloatingActions />
      <Footer />
    </div>
  );
};

export default WalkListPage;
